import {
  chakra,
  forwardRef,
  useToken,
  type HTMLChakraProps,
  type TableCellProps,
} from '@chakra-ui/react';
import { useMemo } from 'react';

import { useTheme } from '@frontend/components';

import { useTableStyles } from '../TableStylesContext';
import { useTableContext } from '../utils/context';
import { useTrContext } from '../utils/rowContext';
import { getCellProps } from './utils';

const relaxedBorderRadiusStyles = {
  left: {
    borderTopLeftRadius: 'sm',
    borderBottomLeftRadius: 'sm',
  },
  right: {
    borderTopRightRadius: 'sm',
    borderBottomRightRadius: 'sm',
  },
};

const RELAXED_PADDING_BY_SIZE: Record<string, number> = {
  sm: 0.75,
  md: 1,
};

export interface TdProps extends TableCellProps {
  direction?: TableCellProps['flexDirection'];
  innerProps?: HTMLChakraProps<'div'>;
  innerRef?: React.RefObject<HTMLDivElement>;
}

export const Td = forwardRef<TdProps, 'td'>(
  (
    {
      as = 'div',
      children,
      direction = 'column',
      innerProps = {},
      innerRef,
      ...restProps
    },
    ref,
  ) => {
    const theme = useTheme();
    const { td: tdStyles, tdInner: tdInnerStyles } = useTableStyles() ?? {};

    const { zebra, density, size } = useTableContext() ?? {};
    const { index: rowIndex } = useTrContext() ?? {};
    const [innerTdProps, outerProps] = getCellProps(restProps);

    const isEvenRow =
      rowIndex != null && rowIndex >= 0 && (rowIndex + 1) % 2 === 0;
    const shouldZebra =
      zebra != null &&
      rowIndex != null &&
      rowIndex >= 0 &&
      (isEvenRow ? zebra === 'even' : zebra === 'odd');

    const tdCss = useMemo(
      () => ({
        ...tdStyles,
        ...(density === 'relaxed'
          ? {
              _first: {
                pl: RELAXED_PADDING_BY_SIZE[size],
                ...relaxedBorderRadiusStyles.left,
                ...(outerProps.width
                  ? {
                      width: `calc(${
                        theme.space[RELAXED_PADDING_BY_SIZE[size]]
                      } + ${
                        theme.space[outerProps.width] ??
                        typeof outerProps.width === 'string'
                          ? outerProps.width
                          : `${outerProps.width}px`
                      })`,
                    }
                  : null),
                ...(outerProps.w
                  ? {
                      w: `calc(${
                        theme.space[RELAXED_PADDING_BY_SIZE[size]]
                      } + ${
                        theme.space[outerProps.w] ??
                        typeof outerProps.w === 'string'
                          ? outerProps.w
                          : `${outerProps.w}px`
                      })`,
                    }
                  : null),
              },
              _last: {
                pr: RELAXED_PADDING_BY_SIZE[size],
                ...relaxedBorderRadiusStyles.right,
              },
            }
          : null),
      }),
      [tdStyles, density, size, outerProps.width, outerProps.w, theme.space],
    );

    const collectedInnerTdProps = {
      flexDirection: direction,
      ...{
        [direction === 'column' ? 'justifyContent' : 'alignItems']: 'center',
        ...(shouldZebra
          ? {
              backgroundColor: 'page.plateLight',
            }
          : null),
      },
      ...innerTdProps,
      ...innerProps,
    };

    const backgroundColor = useToken(
      'colors',
      collectedInnerTdProps.backgroundColor ??
        tdInnerStyles?.backgroundColor ??
        'page.surface',
    );

    return (
      <chakra.td ref={ref} __css={tdCss} {...outerProps}>
        <chakra.div
          as={as}
          __css={tdInnerStyles}
          {...collectedInnerTdProps}
          sx={{
            '--cell-background': backgroundColor,
            ...collectedInnerTdProps.sx,
          }}
          ref={innerRef}
        >
          {children}
        </chakra.div>
      </chakra.td>
    );
  },
);
